import type { EventData } from '@/domains/chat/types/triz-events';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';
import {
    PsHttpService,
    type PatSnapCommonResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { MODULE_CONTENT_KEY, StepType } from '../types';
import type { StepsModuleMessageData } from '@/domains/triz/types';

export interface ContinueJobRequest {
    triz_mind_id: string;
    input: string;
}

export interface ChatJobResponse {
    triz_mind_id: string;
}

export interface SendChatMessageRequest {
    triz_mind_id: string;
    message: string;
    /**
     * 当前选中的历史版本, 可选, 修改场景有值，未传入取最新版本
     */
    reference_version_number?: number;
    /**
     * 引用的模块, 可选, 会在用户消息中原样返回
     */
    reference_module?: MODULE_CONTENT_KEY;
    /**
     * 引用的内容,可选, 会在用户消息中原样返回
     */
    reference_content?: {
        reference_idea_id?: string;
        reference_title?: string;
    };
}

export interface ConfirmMessageRequest {
    triz_mind_id: string;
    message_group_id: string;
    step: StepType;
    source_version_number?: number,
    confirm_message?: {
        // 不太确定是不是这个数据结构
        problems: {
            select: boolean;
            problem_description: string;
            user_friendly_description: string;
        }[];
    };
}

export interface ModifyRequest {
    triz_mind_id: string;
    module_key: MODULE_CONTENT_KEY;
    source_version_number: number;
    data: StepsModuleMessageData;
}

export interface BatchModifyRequest {
    triz_mind_id: string;
    source_version_number: number;
    module_data_list: {
        module_key: MODULE_CONTENT_KEY, data: StepsModuleMessageData
    }[];
}

export interface UploadFileRequest {
    triz_mind_id: string;
    work_project_id: string;
    work_project_item_id: string;
}

/** S3 签名接口请求 */
export interface GetS3SignUrlsRequest {
    object_keys: string[];
    expire: number;
}

/** S3 签名接口返回的单条 */
export interface S3SignedUrlItem {
    sub_resource: string;
    url: string;
}

/** S3 签名接口响应 data */
export interface GetS3SignUrlsResponse {
    resource: string;
    signed_urls: S3SignedUrlItem[];
}

export interface BatchModifyProblemRequest {
    triz_mind_id: string;
    step: number;
    source_version_number?: number;
}

export interface ChatApi {
    createTrizJob(): Promise<ChatJobResponse>;
    getJobStream(data: {
        triz_mind_id: string;
        last_id?: number;
        type?: string;
    }): PsHttpSSE<EventData>;
    sendChatMessage(data: SendChatMessageRequest): Promise<void>;
    confirmMessage(data: ConfirmMessageRequest): Promise<void>;
    editModule(data: ModifyRequest): Promise<void>;
    batchModify(data: BatchModifyRequest): Promise<void>;
    batchModifyProblem(data: BatchModifyProblemRequest): Promise<void>;
    interruptTask(triz_mind_id: string): Promise<void>;
    /** 获取 S3 签名 CDN 地址（图片预览等），走 eureka 域名 */
    getS3SignUrls(objectKeys: string[], expire?: number): Promise<GetS3SignUrlsResponse>;
}

export const ChatApi = Symbol('ChatApi');

@injectable()
export class ChatApiImpl extends PsHttpService implements ChatApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka-rd-agent/triz-mind`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    /**
     * 创建一个TRIZ任务
     */
    createTrizJob() {
        return this.post<PatSnapCommonResponseData<ChatJobResponse>>({
            url: '/create',
            data: {},
        }).then((res) => {
            return res.data as ChatJobResponse;
        });
    }

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    /**
     * 获取TRIZ任务的流
     */
    getJobStream(data: {
        triz_mind_id: string;
        last_id?: number;
        type?: string;
    }) {
        return this._factorySsePsHttp<EventData>()
            .init({
                url: `${this.options.baseURL}/message`,
                data,
                hasFinalMessage: () => true,
            })
            .setFetchHeaders(this.options.headers)
    }

    /**
     * 发送对话消息
     */
    sendChatMessage(data: SendChatMessageRequest) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: '/chat',
            data,
        }).then((res) => {
            return res as unknown as void;
        });
    }

    /**
     * 用户确认问题
     */
    confirmMessage(data: ConfirmMessageRequest) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: '/confirm',
            data,
        }).then((res) => {
            return res as unknown as void;
        });
    }

    /**
     * 用户直接编辑
     */
    batchModify(data: BatchModifyRequest) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: '/batch-modify',
            data,
        }).then((res) => {
            return res as unknown as void;
        });
    }

    /**
     * 用户直接编辑
     */
    editModule(data: ModifyRequest) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: '/modify',
            data,
        }).then((res) => {
            return res as unknown as void;
        });
    }

    /**
     * 重新选择关键问题
     */
    batchModifyProblem(data: BatchModifyProblemRequest) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: '/modify-confirm-problem',
            data,
        }).then((res) => {
            return res as unknown as void;
        });
    }

    /**
     * 暂停/中断任务
     */
    interruptTask(triz_mind_id: string) {
        return this.post<PatSnapCommonResponseData<void>>({
            url: `/interrupt/${triz_mind_id}`,
            data: {},
        }).then((res) => {
            return res as unknown as void;
        });
    }

    /**
     * 获取 S3 签名 CDN 地址（图片预览等），请求 eureka 域名 /eureka/s3/cdn/sign
     */
    async getS3SignUrls(objectKeys: string[], expire = 1800): Promise<GetS3SignUrlsResponse> {
        if (objectKeys.length === 0) {
            return { resource: '', signed_urls: [] };
        }
        const base = import.meta.env.VITE_EUREKA_SERVICE_URI || '';
        const res = await this.post<PatSnapCommonResponseData<GetS3SignUrlsResponse>>({
            url: `${base}/eureka/s3/cdn/sign`,
            data: { object_keys: objectKeys, expire } as GetS3SignUrlsRequest,
        });
        return (res.data as GetS3SignUrlsResponse) ?? { resource: '', signed_urls: [] };
    }
}
